/**
 * The review card deck — the gate.
 *
 * Order is deliberate: what is still UNDECIDED sits at the top (that is the work), what the coach
 * has already decided sits below (that is the record). Nothing that leaves this screen is
 * exportable until it has been confirmed, and the deck owns the two affordances that make that
 * visible: the batch-confirm button and the end-of-deck "Render now?" prompt.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ClipCard } from './ClipCard';
import {
  batchConfirmLabel,
  canExport,
  completionPrompt,
  isResolved,
  pendingQueue,
  reviewSummary,
} from './index';
import { useReviewStore } from '../session/review';

export function CardDeck({ token }: { token: string | null }) {
  const clips = useReviewStore((state) => state.clips);
  const vocabulary = useReviewStore((state) => state.vocabulary);
  const mediaUrl = useReviewStore((state) => state.media?.url ?? null);
  const sourceDuration = useReviewStore((state) => state.sourceDuration);
  const pending = useReviewStore((state) => state.pending);
  const clipErrors = useReviewStore((state) => state.clipErrors);
  const session = useReviewStore((state) => state.session);
  const storedProposed = useReviewStore((state) => state.clipCountProposed);
  const storedConfirmed = useReviewStore((state) => state.clipCountConfirmed);
  const renderRequested = useReviewStore((state) => state.renderRequested);
  const renderError = useReviewStore((state) => state.renderError);
  const decide = useReviewStore((state) => state.decide);
  const confirmAll = useReviewStore((state) => state.confirmAll);
  const requestRender = useReviewStore((state) => state.requestRender);

  const summary = useMemo(() => reviewSummary(clips), [clips]);
  const queue = useMemo(() => pendingQueue(clips), [clips]);
  const decided = useMemo(() => clips.filter(isResolved), [clips]);
  const activeId = queue[0]?.id ?? null;
  const batchLabel = batchConfirmLabel(clips);
  const prompt = completionPrompt(summary);
  const exportOpen = canExport(summary);

  const advanceTo = (clipId: string) => {
    const next = queue.find((clip) => clip.id !== clipId) ?? decided[0];
    if (!next) return;
    window.setTimeout(() => {
      document
        .querySelector(`[data-testid="clip-card"][data-clip-id="${next.id}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 60);
  };

  const onSwipe = (clipId: string, direction: 'keep' | 'discard') => {
    void decide(clipId, { status: direction === 'keep' ? 'confirmed' : 'rejected' }, token).then(
      () => advanceTo(clipId),
    );
  };

  return (
    <section
      data-testid="review-deck"
      data-queue-count={queue.length}
      data-decided-count={decided.length}
      data-confirmed-count={summary.confirmed}
      data-clip-count-proposed={storedProposed}
      data-clip-count-confirmed={storedConfirmed}
      className="space-y-4"
    >
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3">
        <p className="font-mono text-xs text-charcoal-700" data-testid="review-counts">
          {summary.total} clips · {summary.confirmed} confirmed · {summary.awaiting} awaiting ·{' '}
          {summary.rejected} rejected
        </p>
        <p className="font-mono text-xs text-charcoal-500" data-testid="review-d1-counts">
          D1: proposed {storedProposed} · confirmed {storedConfirmed}
        </p>
        <p className="font-mono text-xs text-charcoal-500" data-testid="review-session-status">
          session {session?.status ?? 'unknown'}
        </p>
        {summary.needsAttention > 0 && (
          <p className="text-xs font-semibold text-red-700" data-testid="review-needs-attention">
            {summary.needsAttention} need attention (confidence &lt; 0.70)
          </p>
        )}
        {batchLabel && (
          <button
            type="button"
            data-testid="batch-confirm"
            onClick={() => void confirmAll(token)}
            className="ml-auto rounded bg-charcoal px-3 py-1.5 text-sm font-semibold text-chalk"
          >
            {batchLabel}
          </button>
        )}
      </header>

      {queue.length > 0 ? (
        <ul className="space-y-3" data-testid="review-queue">
          {queue.map((clip) => (
            <li key={clip.id}>
              <ClipCard
                clip={clip}
                vocabulary={vocabulary}
                mediaUrl={mediaUrl}
                sourceDuration={sourceDuration}
                pending={Boolean(pending[clip.id])}
                error={clipErrors[clip.id] ?? null}
                active={clip.id === activeId}
                onConfirm={() => void decide(clip.id, { status: 'confirmed' }, token).then(() => advanceTo(clip.id))}
                onReject={() => void decide(clip.id, { status: 'rejected' }, token).then(() => advanceTo(clip.id))}
                onUndo={() => void decide(clip.id, { status: 'proposed' }, token)}
                onSave={(patch) => void decide(clip.id, patch, token)}
                onSwipe={(direction) => onSwipe(clip.id, direction)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-lg border border-dashed border-charcoal-500/30 px-4 py-6 text-center text-sm text-charcoal-500" data-testid="queue-empty">
          Every clip has a decision.
        </p>
      )}

      {decided.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500" data-testid="decided-heading">
            Decided ({decided.length})
          </h2>
          <ul className="space-y-3" data-testid="review-decided">
            {decided.map((clip) => (
              <li key={clip.id}>
                <ClipCard
                  clip={clip}
                  vocabulary={vocabulary}
                  mediaUrl={mediaUrl}
                  sourceDuration={sourceDuration}
                  pending={Boolean(pending[clip.id])}
                  error={clipErrors[clip.id] ?? null}
                  active={false}
                  onConfirm={() => void decide(clip.id, { status: 'confirmed' }, token)}
                  onReject={() => void decide(clip.id, { status: 'rejected' }, token)}
                  onUndo={() => void decide(clip.id, { status: 'proposed' }, token)}
                  onSave={(patch) => void decide(clip.id, patch, token)}
                  onSwipe={(direction) => onSwipe(clip.id, direction)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4" data-testid="review-export-gate">
        {exportOpen ? (
          <>
            <Link
              to="/export"
              data-testid="download-confirmed"
              className="inline-block rounded bg-copper px-3 py-1.5 text-sm font-semibold text-charcoal"
            >
              Download confirmed clips ({summary.confirmed})
            </Link>
            <p className="mt-2 text-xs text-charcoal-500">
              Only confirmed clips are offered here — {summary.awaiting} still await a decision.
            </p>
          </>
        ) : (
          <p className="text-sm text-charcoal-500" data-testid="export-locked">
            Nothing can be exported yet. Confirm at least one clip — the model proposes, the coach
            decides.
          </p>
        )}
      </div>

      {prompt && (
        <div
          data-testid="review-complete-prompt"
          className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-copper bg-copper-300/20 px-4 py-3"
        >
          <p className="text-sm font-semibold">{prompt}</p>
          {summary.confirmed > 0 && (
            <button
              type="button"
              data-testid="render-now"
              onClick={() => void requestRender(token)}
              disabled={renderRequested}
              className="rounded bg-charcoal px-3 py-1.5 text-sm font-semibold text-chalk disabled:opacity-50"
            >
              {renderRequested ? 'Rendering requested' : 'Render now'}
            </button>
          )}
          {renderRequested && (
            <span className="font-mono text-xs text-charcoal-700" data-testid="render-requested">
              session → {useReviewStore.getState().session?.status ?? 'rendering'}
            </span>
          )}
          {renderError && (
            <span className="text-sm font-medium text-red-700" data-testid="render-error">
              {renderError}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
